import { useState } from "react";
import {
  Landmark,
  Triangle,
  DoorOpen,
  CircleDot,
  Gem,
  ArrowUpNarrowWide,
  Octagon,
  Columns3,
  Check,
  Plus,
} from "lucide-react";
import { Reveal, SectionHeading, Modal } from "./common";
import { ARCH_CARDS, ROMANICO, GOTICO } from "../data/content";

const ICONS = {
  Landmark,
  Triangle,
  DoorOpen,
  CircleDot,
  Gem,
  ArrowUpNarrowWide,
  Octagon,
  Columns3,
};

export const Architettura = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="architettura" className="relative grain bg-ivory py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <SectionHeading
            eyebrow="Capitolo II"
            title="Architettura"
            testid="section-architettura-title"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base sm:text-lg text-taupe leading-relaxed">
              La Basilica è un dialogo tra due linguaggi: il{" "}
              <strong className="text-espresso font-semibold">romanico pugliese</strong>{" "}
              della facciata e il{" "}
              <strong className="text-espresso font-semibold">gotico angioino</strong>{" "}
              delle volte e dello slancio interno, con echi più antichi delle
              culture normanna e bizantina che hanno attraversato il Salento.
              Esplora gli elementi principali: tocca una scheda per
              approfondire.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ARCH_CARDS.map((card, i) => {
            const Icon = ICONS[card.icon];
            return (
              <Reveal key={card.id} delay={(i % 4) * 0.07}>
                <button
                  onClick={() => setSelected(card)}
                  data-testid={`arch-card-${card.id}`}
                  className="group w-full h-full text-left bg-sand border border-stone hover:border-gold transition-all duration-500 hover:shadow-[0_16px_45px_rgba(43,35,31,0.10)] hover:-translate-y-1 overflow-hidden flex flex-col"
                >
                  {card.image ? (
                    <div className="img-frame aspect-[16/10]">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-espresso flex items-center justify-center relative overflow-hidden">
                      <Icon
                        size={52}
                        strokeWidth={1.2}
                        className="text-gold transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 opacity-[0.07]">
                        <div className="absolute -right-6 -bottom-6 w-32 h-32 border border-goldlight rounded-full" />
                        <div className="absolute -right-2 -bottom-2 w-24 h-24 border border-goldlight rounded-full" />
                      </div>
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-serif text-lg sm:text-xl font-semibold text-espresso">
                        {card.title}
                      </h3>
                      <Plus
                        size={16}
                        className="text-gold flex-shrink-0 transition-transform duration-500 group-hover:rotate-90"
                      />
                    </div>
                    <p className="mt-2 text-sm text-taupe leading-relaxed flex-1">
                      {card.brief}
                    </p>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-24">
          <SectionHeading
            eyebrow="Due linguaggi, un edificio"
            title="Romanico e Gotico"
            align="center"
            testid="section-stili-title"
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Reveal delay={0.1}>
              <div className="h-full bg-sand border border-stone p-8 sm:p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-terracotta/60 via-gold to-terracotta/60" />
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-espresso">
                  Romanico pugliese
                </h3>
                <p className="mt-2 font-mono text-xs tracking-[0.2em] uppercase text-terracotta">
                  XI–XIII secolo · la tradizione locale
                </p>
                <ul className="mt-7 space-y-4">
                  {ROMANICO.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-base text-taupe">
                      <Check size={17} className="text-gold mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="h-full bg-espresso border border-espresso p-8 sm:p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-goldlight via-gold to-goldlight" />
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-ivory">
                  Gotico angioino
                </h3>
                <p className="mt-2 font-mono text-xs tracking-[0.2em] uppercase text-goldlight">
                  XIII–XV secolo · la cultura di corte
                </p>
                <ul className="mt-7 space-y-4">
                  {GOTICO.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-base text-ivory/80">
                      <Check size={17} className="text-goldlight mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <p className="mt-10 text-center font-serif text-xl sm:text-2xl italic text-espresso max-w-2xl mx-auto">
              "Nella Basilica i due stili non si scontrano:{" "}
              <span className="text-gold not-italic font-semibold">si sommano</span>."
            </p>
          </Reveal>
        </div>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        image={selected?.image}
        title={selected?.title ?? ""}
        testid="arch-modal"
      >
        <p>{selected?.detail}</p>
      </Modal>
    </section>
  );
};
